import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Grid3X3, Hand, Scissors, Volume2, VolumeX, Globe, Skull, Candy } from "lucide-react";
import axios from "axios";
import TicTacToeBoard from "./TicTacToeBoard";
import ParOuImpar from "./ParOuImpar";
import PedraPapelTesoura from "./PedraPapelTesoura";
import Forca from "./Forca";
import CandyMatch from "./CandyMatch";
import { useLang, STR } from "@/i18n";
import { isMuted, setMuted } from "@/sound";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GameArena() {
  const { lang } = useLang();
  const t = STR[lang];
  const [game, setGame] = useState("candy");
  const [muted, setM] = useState(isMuted());
  const [global, setGlobal] = useState(null);
  const games = [
    { id: "candy", label: t.tab_candy, icon: Candy },
    { id: "velha", label: t.tab_velha, icon: Grid3X3 },
    { id: "parimpar", label: t.tab_parimpar, icon: Hand },
    { id: "rps", label: t.tab_rps, icon: Scissors },
    { id: "forca", label: { pt: "Forca", en: "Hangman", es: "Ahorcado" }[lang], icon: Skull },
  ];

  useEffect(() => {
    let live = true;
    const load = () =>
      axios.get(API + "/score").then((r) => live && setGlobal(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, []);

  const toggleMute = () => {
    setMuted(!muted);
    setM(!muted);
  };

  return (
    <div data-testid="game-arena" className="relative mx-auto max-w-[560px]">
      <span className="spark-star left-[2%] top-[9%]" style={{ "--star-size": "18px" }} />
      <span className="spark-star right-[5%] top-[18%]" style={{ "--star-size": "12px", animationDelay: "0.7s" }} />
      <span className="spark-star left-[14%] bottom-[11%]" style={{ "--star-size": "14px", animationDelay: "1.25s" }} />

      <div className="relative mb-5 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
        {games.map((g) => {
          const active = game === g.id;
          const Icon = g.icon;
          return (
            <button
              key={g.id}
              onClick={() => setGame(g.id)}
              data-testid={"game-tab-" + g.id}
              className={
                "relative flex min-h-12 items-center gap-2 overflow-hidden rounded-[18px] px-3.5 py-1.5 text-[11px] font-black uppercase leading-[1.05] tracking-[0.05em] transition duration-300 active:scale-95 sm:px-4 " +
                (active
                  ? "game-sheen bg-[linear-gradient(135deg,#ffcf55,#ff8a1f_45%,#7dd3fc)] text-[#190735] shadow-[0_18px_36px_-24px_rgba(255,122,26,0.9)]"
                  : "bg-white/10 text-white/60 hover:bg-white/15 hover:text-white")
              }
            >
              {active && (
                <motion.span
                  layoutId="game-tab-active"
                  className="absolute inset-x-3 bottom-1 h-0.5 rounded-full bg-[#190735]/50"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )}
              <Icon className="relative h-4 w-4" strokeWidth={2.25} />
              <span className="relative hidden sm:inline">{g.label}</span>
            </button>
          );
        })}

        <button
          onClick={toggleMute}
          data-testid="sound-toggle-btn"
          aria-pressed={muted}
          aria-label={t.snd_toggle}
          className="grid h-12 w-12 place-items-center rounded-[18px] bg-white/10 text-white/62 transition duration-300 hover:bg-white/15 hover:text-amber-100 active:scale-95"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>

      <div data-testid="global-scoreboard" className="mb-5 flex justify-center">
        <div className="mono flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/52">
          <Globe className="h-3 w-3 text-sky-200" />
          <span>{t.gs_label}</span>
          <span className="h-px w-7 bg-white/18" />
          <span className="text-sky-200">{t.gs_you} {global ? global.you.toLocaleString() : "-"}</span>
          <span className="text-white/25">/</span>
          <span className="text-amber-200">{t.gs_ai} {global ? global.ai.toLocaleString() : "-"}</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={game}
          initial={{ opacity: 0, y: 18, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -14, scale: 0.985 }}
          transition={{ duration: 0.38, ease: [0.16, 1, 0.3, 1] }}
        >
          {game === "candy" ? <CandyMatch /> : game === "velha" ? <TicTacToeBoard /> : game === "parimpar" ? <ParOuImpar /> : game === "rps" ? <PedraPapelTesoura /> : <Forca />}
        </motion.div>
      </AnimatePresence>

      <p data-testid="demo-badge" className="mono mt-7 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-white/42">
        <span className="text-amber-200">{{ pt: "Demo grátis", en: "Free demo", es: "Demo gratis" }[lang]}</span>
        <span className="mx-2 text-white/20">/</span>
        {{ pt: "as partidas valendo créditos acontecem no app", en: "real credit matches happen in the app", es: "las partidas con créditos ocurren en la app" }[lang]}
      </p>
    </div>
  );
}
